'use client';
import {
    useContentfulInspectorMode,
    useContentfulLiveUpdates,
} from '@contentful/live-preview/react';

import ContentLayout from '@/layouts/ContentLayout';
import InsightArticleHeader from './InsightArticleHeader';
import MainBodyToReact from '../../components/MainBodyToReact';
import Link from 'next/link';
import RelatedInsights from '../../components/RelatedInsights';
import {
    TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse,
    TypeInsightsWithoutUnresolvableLinksResponse,
} from '../../_generated';
import { TEntriesResponse } from '../../types/types';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import { TListItem } from '@/types/types';
import { useSettingsContext } from '@/contexts/SettingsContext';
import Script from 'next/script';
import { getArticleSchema } from '@/utils/structuredData';
import { getLocalizedLink } from '@/utils/getLocalizedLink';
import CustomImage from '@/components/CustomImage/CustomImage';

type TInsightArticleProps = {
    pageData: TypeInsightsWithoutUnresolvableLinksResponse;
    relatedInsights: TEntriesResponse<
        TypeInsightsWithAllLocalesAndWithoutUnresolvableLinksResponse[]
    >;
};

const InsightArticle = ({ pageData, relatedInsights }: TInsightArticleProps) => {
    const liveArticle = useContentfulLiveUpdates(pageData);
    const { lang, getLabel } = useSettingsContext();
    const RELATED_INSIGHTS_LABEL = getLabel('label', 'relatedInsights');
    const SEE_ALL_LABEL = getLabel('label', 'seeAll');
    const READ_MORE_LABEL = getLabel('label', 'readMore');
    const WRITTEN_BY_LABEL = getLabel('label', 'writtenBy');
    const articleProps = useContentfulInspectorMode({ entryId: liveArticle.sys.id });
    const authorProps = useContentfulInspectorMode({
        entryId: liveArticle?.fields?.author?.sys.id,
    });

    const articleSchema = getArticleSchema(liveArticle);

    return (
        <>
            <Script
                type="application/ld+json"
                id="article-schema"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(articleSchema),
                }}
            />
            {liveArticle?.fields?.breadcrumbs?.length !== 0 && (
                <Breadcrumbs
                    id={liveArticle?.sys?.id}
                    items={liveArticle?.fields?.breadcrumbs as TListItem[]}
                />
            )}
            <InsightArticleHeader
                id={liveArticle.sys.id}
                title={liveArticle.fields.title}
                author={liveArticle.fields.author}
                publishedDate={liveArticle.fields.publishedDate}
            />

            <ContentLayout>
                <div className="tw-w-full sm:tw-w-3/4 tw-mx-auto">
                    {liveArticle.fields.heroimage?.fields.file?.url && (
                        <CustomImage
                            width={800}
                            height={400}
                            src={`https:${liveArticle.fields.heroimage?.fields.file?.url!}?fm=webp&w=1024&q=100`}
                            alt={liveArticle.fields.heroimage.fields.title!}
                            className="tw-pt-14 tw-pb-8 tw-min-w-full"
                            {...articleProps({
                                fieldId: 'heroimage',
                            })}
                        />
                    )}
                    {liveArticle.fields.mainBody && (
                        <div
                            {...articleProps({
                                fieldId: 'mainBody',
                            })}
                        >
                            <MainBodyToReact body={liveArticle.fields.mainBody} />
                        </div>
                    )}

                    <div
                        className="tw-flex tw-gap-5 tw-pt-[60px] tw-pb-7 tw-border-t tw-border-[#4b4f5433] tw-flex-wrap"
                        {...articleProps({
                            fieldId: 'category',
                        })}
                    >
                        {liveArticle.fields.category.map((category) => (
                            <Link
                                key={category}
                                href={getLocalizedLink(
                                    `/insights?tag=${encodeURIComponent(category.toLowerCase())}`,
                                    lang,
                                )}
                                className="
                                    tw-px-[10px]
                                    tw-py-3
                                    tw-h-[38px]
                                    tw-font-gotham-medium
                                    tw-text-[12px]
                                    tw-bg-[#4b4f541a]
                                    tw-rounded
                                    hover:tw-text-textBase
                                    hover:tw-no-underline"
                            >
                                {category}
                            </Link>
                        ))}
                    </div>

                    <div className="tw-border-t tw-border-[#4b4f5433] tw-py-4">
                        <div className="tw-pb-4 tw-text-base tw-uppercase">{WRITTEN_BY_LABEL}</div>
                        <div className="tw-flex tw-gap-7 md:tw-pb-[130px]">
                            <div className="tw-w-[92px] tw-h-[92px]">
                                <CustomImage
                                    src={
                                        liveArticle.fields.author?.fields.image?.fields.file?.url
                                            ? `https:${liveArticle.fields.author?.fields.image?.fields.file?.url}`
                                            : '/images/user-fallback.svg'
                                    }
                                    alt={
                                        liveArticle.fields.author?.fields.image?.fields?.title ??
                                        'user'
                                    }
                                    height={92}
                                    width={92}
                                    className="tw-h-full tw-w-[92px] tw-rounded-full tw-object-cover"
                                    {...authorProps({
                                        fieldId: 'image',
                                    })}
                                />
                            </div>
                            <div className="tw-flex-1">
                                <div
                                    className="tw-font-gotham-bold tw-text-base"
                                    {...authorProps({
                                        fieldId: 'name',
                                    })}
                                >
                                    {liveArticle.fields.author?.fields.name}
                                </div>
                                {liveArticle.fields.author?.fields.description && (
                                    <div
                                        className="tw-hidden md:tw-block "
                                        {...authorProps({
                                            fieldId: 'description',
                                        })}
                                    >
                                        <MainBodyToReact
                                            body={liveArticle.fields.author?.fields.description}
                                            classes={{
                                                p: 'tw-font-gotham-book tw-text-[#4b4f54] tw-text-[14px] tw-leading-6 tw-py-[10px]',
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {liveArticle.fields.author?.fields.description && (
                            <div
                                className="tw-block md:tw-hidden tw-mt-[20px]"
                                {...authorProps({
                                    fieldId: 'description',
                                })}
                            >
                                <MainBodyToReact
                                    body={liveArticle.fields.author?.fields.description}
                                    classes={{
                                        p: 'tw-font-gotham-book tw-text-[#4b4f54] tw-text-[14px] tw-leading-6 tw-py-[10px]',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ContentLayout>

            {relatedInsights.items.length && (
                <RelatedInsights
                    title={RELATED_INSIGHTS_LABEL}
                    seeAllText={SEE_ALL_LABEL}
                    insights={relatedInsights}
                    readMoreText={READ_MORE_LABEL}
                    className="!tw-pt-20 !tw-pb-12"
                    bg="Gray"
                    flatCards={false}
                    lang={lang}
                />
            )}
        </>
    );
};

export default InsightArticle;
